import { useEffect, useState } from 'react'
import type { TransferCounterpartCandidate } from '@zeni/shared'
import { useLanguage } from '../contexts/LanguageContext'
import { useAmountFormat } from '../contexts/AmountUnitContext'
import { transactionsApi } from '../lib/api'

/** 入力が落ち着くまで待つ時間。金額を打っている間に問い合わせを連発しないため */
const DEBOUNCE_MS = 400

interface TransferCounterpartNoticeProps {
  token: string
  /** 振替先口座ID。未選択なら空文字 */
  toAccountId: string
  /** 振替金額。未入力・不正な入力なら undefined */
  amount: number | undefined
  /** 振替日（YYYY-MM-DD） */
  date: string
  /** 編集中の取引ID。自分自身が相手方として出るのを防ぐ */
  excludeId?: string
  /** 着金側として選んだ入金取引のID。未選択なら null */
  selectedId?: string | null
  /**
   * 選択が変わったときに呼ぶ。渡すと候補を選べるようになる。
   * 自動では紐付けない（同額・近い日付というだけでは別の入金と区別できないため）。
   */
  onSelect?: (id: string | null) => void
}

type State =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'done'; candidates: TransferCounterpartCandidate[]; window: { from: string; to: string } }

function fill(template: string, params: Record<string, string>): string {
  return Object.entries(params).reduce((acc, [key, val]) => acc.replaceAll(`{${key}}`, val), template)
}

/**
 * 振替の相手方（振替先口座に同額が入っている取引）が実在するかを確かめて見せる。
 *
 * 振替は 1 レコードで両側の残高を動かすため、相手方が無くても登録は成り立つ。
 * ここは「本当に着金しているか」の裏取りなので、見つからないときも
 * 保存は止めず、日付・金額を見直す手がかりだけ出す。
 */
export function TransferCounterpartNotice({
  token,
  toAccountId,
  amount,
  date,
  excludeId,
  selectedId = null,
  onSelect,
}: TransferCounterpartNoticeProps) {
  const { t } = useLanguage()
  const { formatCurrency } = useAmountFormat()
  const [state, setState] = useState<State>({ status: 'idle' })

  const ready = !!token && !!toAccountId && amount !== undefined && amount > 0 && !!date

  useEffect(() => {
    if (!ready) {
      setState({ status: 'idle' })
      return
    }
    let alive = true
    setState({ status: 'loading' })
    const timer = setTimeout(() => {
      transactionsApi
        .transferCounterpart(token, { toAccountId, amount, date, excludeId })
        .then((res) => {
          if (alive) setState({ status: 'done', candidates: res.data.candidates, window: res.data.window })
        })
        .catch(() => {
          if (alive) setState({ status: 'error' })
        })
    }, DEBOUNCE_MS)
    return () => {
      alive = false
      clearTimeout(timer)
    }
  }, [ready, token, toAccountId, amount, date, excludeId])

  // 金額や振替先を直すと候補も入れ替わる。選びっぱなしの相手が候補から
  // 消えていたら選択を捨てる（消えた相手を紐付けようとして保存が落ちるのを防ぐ）
  useEffect(() => {
    if (!onSelect || !selectedId) return
    if (state.status !== 'done') return
    if (!state.candidates.some((c) => c.id === selectedId)) onSelect(null)
  }, [state, selectedId, onSelect])

  if (state.status === 'idle') return null

  if (state.status === 'loading') {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {t('transactions_transfer_counterpart_checking')}
      </p>
    )
  }

  if (state.status === 'error') {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400">
        {t('transactions_transfer_counterpart_error')}
      </p>
    )
  }

  if (state.candidates.length === 0) {
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs dark:border-amber-800 dark:bg-amber-950/40">
        <p className="font-medium text-amber-800 dark:text-amber-300">
          {t('transactions_transfer_counterpart_none')}
        </p>
        <p className="mt-0.5 text-amber-800 dark:text-amber-300">
          {fill(t('transactions_transfer_counterpart_none_hint'), {
            from: state.window.from,
            to: state.window.to,
          })}
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-green-300 bg-green-50 p-3 text-xs dark:border-green-800 dark:bg-green-950/40">
      <p className="font-medium text-green-800 dark:text-green-300">
        {fill(t('transactions_transfer_counterpart_found'), { count: String(state.candidates.length) })}
      </p>
      <ul className="mt-1 space-y-1">
        {state.candidates.map((candidate) => {
          // 着金側にできるのは、まだどの振替にも紐付いていない入金だけ
          const selectable = !!onSelect && candidate.kind === 'income' && !candidate.linked
          const summary = (
            <span>
              <span className="tabular-nums">{candidate.date}</span>
              {' '}
              <span className="font-medium tabular-nums">
                +{formatCurrency(candidate.amount, candidate.currency)}
              </span>
              {' '}
              <span className="text-green-700 dark:text-green-400">{candidate.description}</span>
              {candidate.kind === 'transfer_in' && (
                <span className="ml-1 text-green-700 dark:text-green-400">
                  （{fill(t('transactions_transfer_counterpart_via'), { account: candidate.accountName })}）
                </span>
              )}
              {candidate.linked && (
                <span className="ml-1 text-green-700 dark:text-green-400">
                  （{t('transactions_transfer_counterpart_already_linked')}）
                </span>
              )}
              {candidate.excluded && (
                <span className="ml-1 text-green-700 dark:text-green-400">
                  （{t('transactions_excluded_badge')}）
                </span>
              )}
            </span>
          )
          return (
            <li key={candidate.id} className="text-green-800 dark:text-green-300">
              {selectable ? (
                <label className="flex items-start gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    name="transfer-counterpart"
                    className="mt-0.5 shrink-0"
                    checked={selectedId === candidate.id}
                    onChange={() => onSelect(candidate.id)}
                  />
                  {summary}
                </label>
              ) : (
                <span className="flex items-start gap-1.5 pl-[1.125rem]">{summary}</span>
              )}
            </li>
          )
        })}
        {onSelect && (
          <li className="text-green-800 dark:text-green-300">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input
                type="radio"
                name="transfer-counterpart"
                className="shrink-0"
                checked={!selectedId}
                onChange={() => onSelect(null)}
              />
              <span>{t('transactions_transfer_counterpart_link_none')}</span>
            </label>
          </li>
        )}
      </ul>
      {onSelect && (
        <p className="mt-1.5 text-green-800 dark:text-green-300">
          {t('transactions_transfer_counterpart_link_hint')}
        </p>
      )}
    </div>
  )
}
